import {DataFetcher, PriorityConfig} from '@aboutyou/data-fetcher';
import {Product} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  ErrorPageInline,
  PlaceholderTiles,
  mediaDesktopAndUp,
  mediaLargeScreens,
} from '@aboutyou/ui-components';
import {useWishlistCount} from '@aboutyou/user';
import React, {memo} from 'react';
import styled from 'styled-components';
import {
  ShallowRenderingState,
  useShallowRenderingState,
} from '../../../features/ProductTile/utils';
import {WishlistItemGrid} from '../WishlistItemGrid/WishlistItemGrid';
import {
  DeleteItemProvider,
  useWishlistPageStream,
} from '../hooks/useWishlistPageStream';
import {WishlistPageStreamView} from './WishlistPageStreamView';
import {WishlistStreamView} from './WishlistStreamView';

const WishlistStreamViewMemo = memo(WishlistStreamView);
const WishlistPageStreamViewMemo = memo(WishlistPageStreamView);

const WishlistItemStream = ({
  showConfirmOnDeleteItem,
  onClickAddToBasket,
  onClickSoldoutCta,
  gridColumns,
  smallAddToBasketCtaText = false,
  onSimilarProductsClick,
  enableWishlistTabs,
  interactiveHoverMode,
  productTileGalleryMode,
  enableImageGalleryAnimation,
  renderingPriority,
}: {
  showConfirmOnDeleteItem?: boolean;
  onClickAddToBasket: ({
    productId,
    product,
    brandName,
  }: {
    productId: number;
    product?: Product;
    brandName: string;
  }) => void;
  onClickSoldoutCta: (productId: number) => void;
  onSimilarProductsClick: (product: Product) => void;
  gridColumns?: number;
  smallAddToBasketCtaText?: boolean;
  enableWishlistTabs?: boolean;
  interactiveHoverMode?: boolean;
  productTileGalleryMode?: 'hover' | 'always';
  enableImageGalleryAnimation?: boolean;
} & PriorityConfig) => {
  return (
    <WishlistStream
      gridColumns={gridColumns}
      onClickAddToBasket={onClickAddToBasket}
      onClickSoldoutCta={onClickSoldoutCta}
      showConfirmOnDeleteItem={showConfirmOnDeleteItem}
      smallAddToBasketCtaText={smallAddToBasketCtaText}
      onSimilarProductsClick={onSimilarProductsClick}
      enableWishlistTabs={enableWishlistTabs}
      interactiveHoverMode={interactiveHoverMode}
      productTileGalleryMode={productTileGalleryMode}
      enableImageGalleryAnimation={enableImageGalleryAnimation}
      renderingPriority={renderingPriority}
    />
  );
};
const WishlistItemStreamMemo = memo(WishlistItemStream);
export {WishlistItemStreamMemo as WishlistItemStream};

const HISTORY_STATE_LAST_VISIBLE_ITEMS_KEY =
  'wishlist_product_stream_last_visible_items';

export interface WishlistStreamProps {
  showConfirmOnDeleteItem?: boolean;
  onClickAddToBasket: ({
    productId,
    product,
    brandName,
  }: {
    productId: number;
    product?: Product;
    brandName: string;
  }) => void;
  onClickSoldoutCta: (productId: number) => void;
  onSimilarProductsClick: (product: Product) => void;
  gridColumns?: number;
  smallAddToBasketCtaText: boolean;
  enableWishlistTabs?: boolean;
  interactiveHoverMode?: boolean;
  productTileGalleryMode?: 'hover' | 'always';
  enableImageGalleryAnimation?: boolean;
}

const WishlistStream = (props: WishlistStreamProps & PriorityConfig) => {
  const {t} = useTranslation();

  const shallowRenderingState = useShallowRenderingState({
    uniqueKey: HISTORY_STATE_LAST_VISIBLE_ITEMS_KEY,
  });

  return (
    <PageDataFetcher
      errorHeadline={t('Oops! Something went wrong!')}
      props={props}
      shallowRenderingState={shallowRenderingState}
    />
  );
};

const PageDataFetcher = ({
  props,
  shallowRenderingState,
  errorHeadline,
}: {
  props: WishlistStreamProps & PriorityConfig;
  shallowRenderingState?: ShallowRenderingState;
  errorHeadline: string;
}) => {
  const {loadItems, deleteItem} = useWishlistPageStream();

  return (
    <DeleteItemProvider deleteItem={deleteItem}>
      <DataFetcher
        request={loadItems}
        Loading={LoadingComponent}
        Error={() => (
          <ErrorPosition>
            <ErrorPageInline headline={errorHeadline} url="/" />
          </ErrorPosition>
        )}
        View={
          props.enableWishlistTabs
            ? WishlistPageStreamViewMemo
            : WishlistStreamViewMemo
        }
        viewProps={{
          ...props,
          shallowRenderingState,
          isDataFetching: loadItems.isFetching,
          isDataFetched: loadItems.isFetched,
        }}
        renderingPriority={props.renderingPriority}
      />
    </DeleteItemProvider>
  );
};

const LoadingComponent = () => {
  const {count} = useWishlistCount();

  // Should avoid rendering the footer before the product stream
  return (
    <WishlistItemGrid>
      <PlaceholderTiles amount={count < 24 ? 24 : count} ctaVisible />
    </WishlistItemGrid>
  );
};

const ErrorPosition = styled.div`
  grid-column: span 2;
  @media ${mediaDesktopAndUp} {
    grid-column: span 3;
  }

  @media ${mediaLargeScreens} {
    grid-column: span 4;
  }
`;
